import { GroupedNodes, InputValue, NodeStruct } from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';

/**
 * Judgment-gate pattern stamp: one-click insert of the canonical
 *
 *   Classify / Decide → Validate / Assert → Switch / Match
 *
 * pre-wired chain (classify.result → validate.value, validate.value →
 * switch.value) with Switch cases synced from the classify node's
 * decision_labels. Conditional edge labels are then derived from those cases
 * by the existing branch-handle flow when the author attaches downstream
 * nodes, so labels stay consistent end to end.
 *
 * Wiring replicates the live "Example — Classify + Validate + Switch"
 * workflow; everything goes through existing node-store APIs (addNodes,
 * handleParameterChange, onConnect) — no new graph primitives.
 */

export const JUDGMENT_GATE_NODE_IDS = {
  classify: 'classify_decide_node',
  validate: 'validate_node',
  switch: 'switch_node',
} as const;

export interface JudgmentGateStructs {
  classify: NodeStruct;
  validate: NodeStruct;
  switch: NodeStruct;
}

export const DEFAULT_JUDGMENT_LABELS = ['match', 'no_match'];

/** Horizontal spacing between stamped nodes (node width ~300 + gap). */
const STAMP_H_GAP = 380;

/** Locate the three catalog structs; null when any is unavailable (e.g. the
 * classify node is feature-flagged off for this org). */
export function findJudgmentGateStructs(
  groupedNodes: GroupedNodes,
): JudgmentGateStructs | null {
  const all = Object.values(groupedNodes).flat();
  const byNodeId = (nodeId: string): NodeStruct | undefined =>
    all.find((struct) => struct.node_id === nodeId);

  const classify = byNodeId(JUDGMENT_GATE_NODE_IDS.classify);
  const validate = byNodeId(JUDGMENT_GATE_NODE_IDS.validate);
  const switchStruct = byNodeId(JUDGMENT_GATE_NODE_IDS.switch);
  if (!classify || !validate || !switchStruct) return null;
  return { classify, validate, switch: switchStruct };
}

/** Read the classify catalog struct's decision labels (value, else default,
 * else the binary default). */
export function getClassifyDecisionLabels(struct: NodeStruct): string[] {
  const param = struct.params?.find((p) => p.field_id === 'decision_labels');
  const candidates = [param?.value, param?.default];
  for (const candidate of candidates) {
    if (
      Array.isArray(candidate) &&
      candidate.length > 0 &&
      candidate.every((label) => typeof label === 'string')
    ) {
      return [...(candidate as string[])];
    }
  }
  return [...DEFAULT_JUDGMENT_LABELS];
}

/** One Switch case per decision label, comparing the validated envelope's
 * decision (path value→value→decision, matching the live example wiring). */
export function buildSwitchCases(
  labels: string[],
): Array<Record<string, unknown>> {
  return labels.map((label) => ({
    label,
    when: {
      op: 'eq',
      left: { op: 'path', path: ['value', 'value', 'decision'] },
      right: { op: 'literal', value: label },
    },
  }));
}

/** JSON Schema asserting the classify_decide.result.v1 envelope with the
 * decision enum synced to the configured labels. profile_version is required
 * but unpinned (the classify node emits an explicit 'unversioned' sentinel
 * when the author does not version profiles). */
export function buildValidateEnvelopeSchema(
  labels: string[],
): Record<string, unknown> {
  return {
    type: 'object',
    required: ['decision', 'result', 'metadata'],
    additionalProperties: true,
    properties: {
      decision: { type: 'string', enum: [...labels] },
      result: {
        type: 'object',
        required: [
          'decision',
          'profile_version',
          'prompt_digest',
          'schema_version',
        ],
        additionalProperties: true,
        properties: {
          decision: { type: 'string', enum: [...labels] },
          profile_version: { type: 'string', minLength: 1 },
          prompt_digest: { type: 'string', minLength: 8 },
          schema_version: {
            type: 'string',
            const: 'classify_decide.result.v1',
          },
        },
      },
      metadata: { type: 'object' },
    },
  };
}

export interface JudgmentGateStampResult {
  classifyId: string;
  validateId: string;
  switchId: string;
}

/**
 * Insert and wire the pattern at `position` using existing store actions.
 * Returns the new node ids, or null when the structs' ports/params don't
 * expose the expected wiring surface (fail closed, stamp nothing).
 */
export function stampJudgmentGate(
  structs: JudgmentGateStructs,
  position: { x: number; y: number },
): JudgmentGateStampResult | null {
  const classifyResultPort = structs.classify.output_ports?.find(
    (port) => port.name === 'result',
  );
  const validateValuePort = structs.validate.params?.find(
    (param) => param.field_id === 'value',
  )?.bindable_config?.input_port;
  const validateValueOutPort = structs.validate.output_ports?.find(
    (port) => port.name === 'value',
  );
  const switchValuePort = structs.switch.params?.find(
    (param) => param.field_id === 'value',
  )?.bindable_config?.input_port;
  if (
    !classifyResultPort ||
    !validateValuePort ||
    !validateValueOutPort ||
    !switchValuePort
  ) {
    console.warn(
      '[judgment-gate-stamp] Catalog structs are missing expected ports; not stamping.',
    );
    return null;
  }

  const store = useNodeStore.getState();
  const labels = getClassifyDecisionLabels(structs.classify);

  const [classifyId, validateId, switchId] = store.addNodes([
    { struct: structs.classify, position },
    {
      struct: structs.validate,
      position: { x: position.x + STAMP_H_GAP, y: position.y },
    },
    {
      struct: structs.switch,
      position: { x: position.x + 2 * STAMP_H_GAP, y: position.y },
    },
  ]);

  // Label-synced configs: Switch cases mirror classify decision_labels, and
  // the Validate schema pins the decision enum to the same labels.
  store.handleParameterChange(
    validateId,
    'schema',
    buildValidateEnvelopeSchema(labels) as unknown as InputValue,
  );
  store.handleParameterChange(
    validateId,
    'error_code',
    'judgment.envelope.invalid',
  );
  store.handleParameterChange(
    validateId,
    'error_message',
    'Classifier decision envelope failed validation',
  );
  store.handleParameterChange(
    switchId,
    'cases',
    buildSwitchCases(labels) as unknown as InputValue,
  );

  // classify.result → validate.value, then validate.value → switch.value.
  // onConnect derives PORT_TO_PARAM + target_param_id and flips is_bound.
  store.onConnect({
    source: classifyId,
    sourceHandle: classifyResultPort.id,
    target: validateId,
    targetHandle: validateValuePort.id,
  });
  store.onConnect({
    source: validateId,
    sourceHandle: validateValueOutPort.id,
    target: switchId,
    targetHandle: switchValuePort.id,
  });

  return { classifyId, validateId, switchId };
}
